/**
 * Thin PostHog wrapper. PostHog is loaded as a `<script>` in public/index.html
 * (window.posthog). This module gives us:
 *   - `track(name, props)`  — safe fire-and-forget event capture
 *   - `useScrollCompletion(target, threshold, onComplete)` — one-shot hook
 *     that fires `onComplete` when the user has scrolled `threshold` (0-1) of
 *     the way down `target` (a ref) — used for "content-consumed" funnel events
 *     (blog reads, case-study reads).
 *
 * All helpers no-op safely if PostHog isn't loaded (ad-blockers, dev, etc.),
 * so the app never breaks because of analytics.
 */
import { useEffect, useRef } from "react";

export function track(event, properties = {}) {
  try {
    if (typeof window === "undefined") return;
    const ph = window.posthog;
    if (!ph || typeof ph.capture !== "function") return;
    ph.capture(event, properties);
  } catch {
    /* swallow — analytics must never break UX */
  }
}

/**
 * Fires `onComplete` once when the user has scrolled at least `threshold` of
 * the way through the element referenced by `targetRef`. Uses IntersectionObserver
 * so it's cheap on scroll-heavy pages.
 *
 * @param {React.RefObject} targetRef  - element whose scroll depth we watch
 * @param {number} threshold           - 0..1 (default 0.8 → 80% viewed)
 * @param {Function} onComplete        - callback fired at most once
 */
export function useScrollCompletion(targetRef, threshold = 0.8, onComplete) {
  const firedRef = useRef(false);
  useEffect(() => {
    firedRef.current = false;
    const el = targetRef?.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;

    const onScroll = () => {
      if (firedRef.current) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height + window.innerHeight * 0.1;
      const passed = window.innerHeight - rect.top;
      const ratio = Math.max(0, Math.min(1, passed / total));
      if (ratio >= threshold) {
        firedRef.current = true;
        try { onComplete && onComplete(); } catch {}
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Also run once in case content is already fully visible on load
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [targetRef, threshold, onComplete]);
}
